<template>
  <div
    class="card card-custom card-stretch p-0 d-flex flex-column"
    id="cardTimeline"
  >
    <v-overlay :value="carregandoInicial" :absolute="true">
      <v-progress-circular indeterminate size="32" color="#462181" width="2"
        ><v-progress-circular
          indeterminate
          size="24"
          color="#ff7f00"
          width="2"
          rotate="180"
        ></v-progress-circular
      ></v-progress-circular>
    </v-overlay>

    <!-- Cabecalho fixo -->
    <div class="tl-cabecalho">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <span class="card-titulo-icone"><i class="fas fa-clock"></i></span>
          <h5 class="m-0 ml-3 mr-2">
            {{ variant === "expanded" ? "Timeline da conta" : "Timeline" }}
          </h5>
          <Tooltip
            message="A Timeline exibe envios, respostas, agendamentos e erros em ordem cronológica. Use os filtros para selecionar os tipos de evento desejados."
          />
        </div>

        <!-- Compacto: expandir na propria linha do titulo. -->
        <button
          v-if="variant === 'compact'"
          type="button"
          class="tl-filtro tl-filtro--expandir"
          title="Expandir timeline"
          @click="$emit('expandir')"
        >
          <i class="fas fa-expand"></i>
        </button>

        <div
          v-else
          class="d-flex align-items-center"
          style="position: relative"
        >
          <a
            class="btn btn-sm btn-light mr-2"
            style="background: #472282; color: #fff; white-space: nowrap"
            @click.stop.prevent="$refs.periodoPicker.togglePicker(true)"
          >
            <span class="font-size-base">{{ rotuloPeriodo }}</span>
          </a>
          <date-range-picker
            ref="periodoPicker"
            opens="left"
            :always-show-calendars="false"
            :ranges="periodoRanges"
            :locale-data="localizacaoPicker"
            :auto-apply="true"
            v-model="periodoSelecionado"
            @select="carregarPrimeiraPagina"
          ></date-range-picker>
          <button
            type="button"
            class="tl-cabecalho__fechar"
            title="Fechar"
            @click="$emit('fechar')"
          >
            <i class="fas fa-xmark"></i>
          </button>
        </div>
      </div>

      <div v-if="variant === 'expanded'" class="tl-busca mt-3">
        <b-form-input
          v-model="buscaDigitada"
          placeholder="Buscar campanha, modelo ou erro"
          size="sm"
          @input="debounceBusca"
        ></b-form-input>
        <button
          v-if="buscaDigitada"
          type="button"
          class="tl-busca__limpar"
          title="Limpar busca"
          @click="limparBusca"
        >
          <i class="fas fa-xmark"></i>
        </button>
      </div>

      <div class="tl-filtros">
        <button
          v-for="filtro in FILTROS"
          :key="filtro.tipo"
          type="button"
          class="tl-filtro"
          :style="
            tiposAtivos.includes(filtro.tipo)
              ? {
                  background: filtro.fundo,
                  borderColor: filtro.cor,
                  color: filtro.cor
                }
              : {}
          "
          @click="alternarTipo(filtro.tipo)"
        >
          <i :class="['fas', filtro.icone]" :style="{ color: filtro.cor }"></i>
          {{ filtro.label }}
        </button>

        <button
          v-if="tiposAtivos.length"
          type="button"
          class="tl-filtro tl-filtro--limpar"
          title="Limpar filtros"
          @click="limparFiltros"
        >
          <i class="fas fa-xmark"></i>
        </button>
      </div>
    </div>

    <!-- Corpo: lista + (opcional) painel lateral injetado por quem usa o
    componente. Como o cabecalho fica acima deste bloco, ele ocupa a
    largura toda e o lateral se alinha so com a lista. -->
    <div class="tl-corpo">
      <!-- O fade fica no wrapper, nao no proprio scroller: dentro do
      perfect-scrollbar o pseudo-elemento e posicionado em relacao ao
      conteudo e sobe junto com a rolagem. -->
      <div class="tl-corpo__lista-wrap">
        <perfect-scrollbar ref="scrollbar" class="tl-corpo__lista">
          <div class="tl">
            <template v-for="(evento, i) in eventos">
              <div
                v-if="mostrarSeparador(i)"
                :key="`sep-${i}`"
                class="tl__separador"
              >
                <span>{{ separadorData(evento.data) }}</span>
              </div>

              <div
                :key="`ev-${i}`"
                class="tl__item"
                :class="[
                  i % 2 === 0 ? 'tl__item--esq' : 'tl__item--dir',
                  `tl__item--${evento.tipo}`
                ]"
              >
                <div class="tl__meio">
                  <span class="tl__hora">{{ hora(evento.data) }}</span>
                  <span class="tl__bolha">
                    <i :class="['fas', config(evento.tipo).icone]"></i>
                  </span>
                </div>

                <div
                  class="tl__card"
                  :class="{ 'tl__card--clicavel': variant === 'expanded' }"
                  @click="
                    variant === 'expanded' &&
                      $emit('evento:selecionado', evento)
                  "
                >
                  <div class="tl__tipo">
                    <i :class="['fas', config(evento.tipo).icone]"></i>
                    {{ config(evento.tipo).label }}
                  </div>

                  <div class="tl__linha">
                    <span class="tl__rotulo">Campanha:</span>
                    <span
                      class="tl__valor"
                      v-blur-sensitive
                      :title="evento.modelo"
                      >{{ evento.modelo }}</span
                    >
                  </div>

                  <div class="tl__linha" v-if="evento.tipo === 'erro'">
                    <span class="tl__rotulo">Motivo:</span>
                    <span
                      class="tl__valor"
                      v-blur-sensitive
                      :title="motivo(evento)"
                      >{{ motivo(evento) }}</span
                    >
                  </div>
                  <div
                    class="tl__linha"
                    v-else-if="evento.tipo === 'agendamento'"
                  >
                    <span class="tl__rotulo">Agendado para:</span>
                    <span class="tl__valor">{{
                      agendadoPara(evento.data)
                    }}</span>
                  </div>
                  <div class="tl__linha" v-else>
                    <span class="tl__rotulo">Modelo:</span>
                    <span
                      class="tl__valor"
                      v-blur-sensitive
                      :title="evento.campanha"
                      >{{ evento.campanha }}</span
                    >
                  </div>

                  <div class="tl__rodape">
                    <span
                      v-if="
                        config(evento.tipo).textoQuantidade &&
                        evento.quantidade > 0
                      "
                      class="tl__badge"
                    >
                      <i :class="['fas', config(evento.tipo).icone]"></i>
                      {{ evento.quantidade }}
                    </span>
                    <span
                      v-if="evento.qtd_enviado_erro > 0"
                      class="tl__badge tl__badge--erro"
                    >
                      <i class="fas fa-circle-exclamation"></i>
                      {{ evento.qtd_enviado_erro }}
                    </span>

                    <b-button
                      v-if="
                        evento.tipo === 'erro' &&
                        evento.agendada_id &&
                        !evento._reenviado
                      "
                      variant="warning"
                      size="sm"
                      class="tl__reenviar"
                      @click.stop="executarCampanhaNovamente(evento)"
                    >
                      Reenviar
                    </b-button>
                    <small v-else-if="evento._reenviado" class="tl__reenviado">
                      <i class="fas fa-check mr-1"></i>Reenviado
                    </small>

                    <!-- Atalhos diretos: um por destino, so quando o evento
                    tem o dado necessario pra aquele destino. -->
                    <span class="tl__acoes">
                      <button
                        v-for="destino in DESTINOS_LISTA"
                        :key="destino.chave"
                        v-show="temDestino(evento, destino.chave)"
                        type="button"
                        class="tl__acao"
                        :title="destino.titulo"
                        @click.stop="irParaDestino(evento, destino.chave)"
                      >
                        <i :class="['fas', destino.icone]"></i>
                      </button>
                    </span>

                    <i
                      v-if="variant === 'expanded'"
                      class="fas fa-chevron-right tl__seta"
                    ></i>
                  </div>
                </div>
              </div>
            </template>

            <div ref="sentinela" class="tl__sentinela"></div>

            <div v-if="carregandoMais" class="tl__status">
              <small>Carregando mais eventos...</small>
            </div>
            <div v-else-if="fimDoPeriodo && eventos.length" class="tl__status">
              <small>Não existem mais eventos no período.</small>
            </div>
            <div
              v-else-if="!carregandoInicial && !eventos.length"
              class="tl__status"
            >
              <small>Nenhum evento no período.</small>
            </div>
          </div>
        </perfect-scrollbar>
      </div>

      <div v-if="$slots.lateral" class="tl-corpo__lateral">
        <slot name="lateral"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Swal from "sweetalert2";
import API_LINKS from "../automacao/api.links";
import ApiService from "@/core/services/api.service";
import Tooltip from "./Tooltip.vue";
import DateRangePicker from "vue2-daterange-picker";
import { DESTINOS, temDestino, urlDestino } from "./timelineDestinos";

// Cores mantidas as do dashboard (mesmas do grafico Mensagens):
// laranja = envio, roxo = resposta, vermelho = erro, cinza = agendado.
const FILTROS = [
  {
    tipo: "envio",
    label: "Envio",
    icone: "fa-paper-plane",
    cor: "#ff7f00",
    fundo: "#fff3e5",
    textoQuantidade: "envios"
  },
  {
    tipo: "recebimento",
    label: "Resposta",
    icone: "fa-comment-dots",
    cor: "#472282",
    fundo: "#efe9f7",
    textoQuantidade: "respostas"
  },
  {
    tipo: "erro",
    label: "Erro",
    icone: "fa-circle-exclamation",
    cor: "#f64e60",
    fundo: "#fde6e8",
    textoQuantidade: "notificações"
  },
  {
    tipo: "agendamento",
    label: "Agendamento",
    icone: "fa-clock",
    // Verde do WhatsApp, o mesmo ja usado em GraficoLinha/EstatisticasCredito.
    cor: "#00a884",
    fundo: "#e3f5f0",
    textoQuantidade: ""
  }
];

const CONFIG_POR_TIPO = FILTROS.reduce((acc, f) => {
  acc[f.tipo] = f;
  return acc;
}, {});

const LOCALIZACAO_PICKER = {
  format: "dd/mm/yyyy",
  separator: " - ",
  applyLabel: "Aplicar",
  cancelLabel: "Cancelar",
  fromLabel: "De",
  toLabel: "Para",
  customRangeLabel: "Personalizado",
  weekLabel: "S",
  daysOfWeek: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  monthNames: [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro"
  ],
  firstDay: 1
};

export default {
  name: "Timeline",
  components: { Tooltip, DateRangePicker },
  props: {
    variant: {
      type: String,
      default: "compact",
      validator: (v) => ["compact", "expanded"].includes(v)
    },
    contaId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      eventos: [],
      proximoCursor: null,
      fimDoPeriodo: false,
      carregandoInicial: false,
      carregandoMais: false,
      tiposAtivos: [],
      buscaDigitada: "",
      busca: "",
      debounceTimeout: null,
      periodoSelecionado: {
        startDate: moment().subtract(1, "days").toDate(),
        endDate: moment().toDate()
      },
      localizacaoPicker: LOCALIZACAO_PICKER,
      FILTROS,
      DESTINOS_LISTA: [DESTINOS.campanha, DESTINOS.relatorio]
    };
  },
  computed: {
    periodoRanges() {
      const hoje = moment().toDate();
      return {
        "1 dia": [moment().subtract(1, "days").toDate(), hoje],
        "3 dias": [moment().subtract(3, "days").toDate(), hoje],
        "7 dias": [moment().subtract(7, "days").toDate(), hoje],
        "15 dias": [moment().subtract(15, "days").toDate(), hoje],
        "30 dias": [moment().subtract(30, "days").toDate(), hoje]
      };
    },
    rotuloPeriodo() {
      return (
        moment(this.periodoSelecionado.startDate).format("DD/MM/YYYY") +
        " - " +
        moment(this.periodoSelecionado.endDate).format("DD/MM/YYYY")
      );
    }
  },
  mounted() {
    this.carregarPrimeiraPagina();
    this.$nextTick(() => this.iniciarObservador());
  },
  beforeDestroy() {
    this.pararObservador();
    clearTimeout(this.debounceTimeout);
  },
  methods: {
    temDestino,

    // Com contaId estamos olhando a timeline de outra conta (Central de
    // Contas): navegar direto levaria pra rota da conta logada, entao quem
    // resolve e o pai, que sabe personificar antes.
    irParaDestino(evento, destino) {
      if (!temDestino(evento, destino)) return;

      if (this.contaId) {
        this.$emit("evento:ir", { evento, destino });
        return;
      }

      this.$router.push(urlDestino(evento, destino));
    },

    config(tipo) {
      return CONFIG_POR_TIPO[tipo] || CONFIG_POR_TIPO.agendamento;
    },

    hora(data) {
      return moment(data).format("HH:mm");
    },

    separadorData(data) {
      return moment(data).format("DD MMM");
    },

    // Separador aparece no primeiro evento e sempre que vira o dia.
    mostrarSeparador(indice) {
      if (indice === 0) return true;
      return !moment(this.eventos[indice].data).isSame(
        this.eventos[indice - 1].data,
        "day"
      );
    },

    agendadoPara(data) {
      return moment(data).format("DD MMM HH:mm");
    },

    // Para erro de campanha existe o rotulo real do status; nas
    // desconexoes de whatsapp/agente o proprio texto ja e o motivo.
    motivo(evento) {
      return evento.motivo_erro || evento.campanha;
    },

    endpoint() {
      return this.contaId
        ? `central-contas/${this.contaId}/timeline`
        : "dashboard/timeline";
    },

    parametros() {
      const params = {
        tipos: this.tiposAtivos,
        limite: 10
      };

      if (this.busca) {
        params.busca = this.busca;
      }

      if (this.proximoCursor) {
        params.cursor = this.proximoCursor;
      }

      if (this.variant === "expanded") {
        params.periodo_inicio = moment(this.periodoSelecionado.startDate)
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        params.periodo_fim = moment(this.periodoSelecionado.endDate)
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
      }

      return params;
    },

    // WEL-5194: reinicia cursor/lista — usado no mount, ao trocar filtro,
    // busca ou período. Mantém os filtros de tipo já selecionados.
    carregarPrimeiraPagina() {
      this.eventos = [];
      this.proximoCursor = null;
      this.fimDoPeriodo = false;
      this.carregandoInicial = true;

      ApiService.get(this.endpoint(), "", false, { params: this.parametros() })
        .then((response) => {
          const dados = response.data.data;
          if (dados) {
            this.eventos = dados.timeline || [];
            this.proximoCursor = dados.proximoCursor || null;
            this.fimDoPeriodo = !this.proximoCursor;
          }
        })
        .finally(() => {
          this.carregandoInicial = false;
          this.$nextTick(() => this.iniciarObservador());
        });
    },

    carregarMais() {
      // carregandoInicial tambem barra: sem isso, rolar enquanto a primeira
      // pagina ainda esta vindo dispara um append com o cursor antigo, que
      // logo em seguida e' descartado pelo reset da primeira pagina.
      if (
        this.carregandoMais ||
        this.carregandoInicial ||
        this.fimDoPeriodo ||
        !this.proximoCursor
      ) {
        return;
      }

      const cursorDaRequisicao = this.proximoCursor;
      this.carregandoMais = true;

      ApiService.get(this.endpoint(), "", false, { params: this.parametros() })
        .then((response) => {
          const dados = response.data.data;
          if (dados) {
            this.eventos = this.eventos.concat(dados.timeline || []);

            // Se o backend devolver o mesmo cursor, continuar pedindo so
            // repete a mesma pagina pra sempre -- encerra como fim.
            const mesmoCursor =
              JSON.stringify(dados.proximoCursor) ===
              JSON.stringify(cursorDaRequisicao);

            this.proximoCursor = mesmoCursor
              ? null
              : dados.proximoCursor || null;
            this.fimDoPeriodo = !this.proximoCursor;
          }
        })
        .catch(() => {
          // Sem isto o sentinela continua intersectando e a falha vira um
          // laco de retentativa contra o backend.
          this.fimDoPeriodo = true;
        })
        .finally(() => {
          this.carregandoMais = false;
          // O sentinela pode continuar intersectando depois do append (se o
          // lote novo não empurrar altura suficiente pra sair da viewport) —
          // sem reobservar, o IntersectionObserver não dispara de novo no
          // próximo scroll (só reage a MUDANÇA de interseção).
          this.$nextTick(() => this.iniciarObservador());
        });
    },

    iniciarObservador() {
      this.pararObservador();
      const sentinela = this.$refs.sentinela;
      if (!sentinela || typeof IntersectionObserver === "undefined") {
        return;
      }
      const root = this.$refs.scrollbar?.$el || null;
      this.observador = new IntersectionObserver(
        (entradas) => {
          if (entradas.some((e) => e.isIntersecting)) {
            this.carregarMais();
          }
        },
        { root, rootMargin: "150px" }
      );
      this.observador.observe(sentinela);
    },

    pararObservador() {
      if (this.observador) {
        this.observador.disconnect();
        this.observador = null;
      }
    },

    alternarTipo(tipo) {
      const indice = this.tiposAtivos.indexOf(tipo);
      if (indice === -1) {
        this.tiposAtivos.push(tipo);
      } else {
        this.tiposAtivos.splice(indice, 1);
      }
      this.carregarPrimeiraPagina();
    },

    limparFiltros() {
      this.tiposAtivos = [];
      this.carregarPrimeiraPagina();
    },

    limparBusca() {
      clearTimeout(this.debounceTimeout);
      this.buscaDigitada = "";
      this.busca = "";
      this.carregarPrimeiraPagina();
    },

    debounceBusca(valor) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.busca = valor;
        this.carregarPrimeiraPagina();
      }, 300);
    },

    executarCampanhaNovamente(evento) {
      const agendadaId = evento.agendada_id;
      Swal.fire({
        title: "Deseja tentar novamente?",
        text: "A campanha será enviada novamente.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, tentar novamente!",
        cancelButtonText: "Cancelar"
      }).then((result) => {
        if (result.value) {
          ApiService.post(API_LINKS.campanha + "/tentar-novamente", {
            agendada_id: agendadaId
          }).then(() => {
            this.$set(evento, "_reenviado", true);
            Swal.fire(
              "Tentativa realizada!",
              "A campanha foi enviada novamente.",
              "success"
            );
          });
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.tl-cabecalho {
  flex: 0 0 auto;
  padding: 1.25rem 1.25rem 0.75rem;
}

.tl-cabecalho__fechar {
  border: none;
  background: none;
  color: #b5b5c3;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  line-height: 1;

  &:hover {
    color: #472282;
  }
}

.tl-busca {
  position: relative;

  input {
    padding-right: 1.9rem;
  }
}

.tl-busca__limpar {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #b5b5c3;
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #472282;
  }
}

.tl-filtros {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.35rem;
  margin-top: 0.85rem;
}

.tl-filtro {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  height: 28px;
  padding: 0 0.6rem;
  border-radius: 8px;
  border: 1.5px solid #e4e6ef;
  background: #fff;
  color: #7c7c8a;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 0.8rem !important;
  }

  &:hover {
    border-color: #b5b5c3;
  }
}

// So icone: largura igual a altura, pra ficar quadrado do mesmo tamanho
// dos chips com texto.
.tl-filtro--limpar,
.tl-filtro--expandir {
  width: 28px;
  padding: 0;
}

.tl-filtro--limpar {
  background: #f3f6f9;
  border-color: #e4e6ef;

  &:hover {
    color: #f64e60;
    border-color: #f64e60;
  }
}

.tl-filtro--expandir:hover {
  color: #472282;
  border-color: #472282;
}

// Corpo: lista (+ lateral opcional). O cabecalho fica fora daqui, por
// isso ocupa a largura toda do card.
.tl-corpo {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}

// O fade no rodape faz o conteudo "apagar" ao chegar na base, em vez de
// cortar seco na borda do card. Fica sobre a lista, sem capturar clique.
// Wrapper que NAO rola: o fade mora aqui pra ficar ancorado na base
// visivel. Dentro do perfect-scrollbar ele e posicionado em relacao ao
// conteudo e sobe junto com a rolagem.
.tl-corpo__lista-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  // Faixa branca abaixo da lista: fecha o card e evita a sensacao de
  // conteudo flutuando ate o limite.
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #ebedf3;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 32px;
    pointer-events: none;
    z-index: 3;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #fff 80%);
  }
}

.tl-corpo__lista {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

// So existe quando quem usa o componente injeta o slot lateral.
.tl-corpo__lateral {
  flex: 0 0 60%;
  min-width: 0;
  border-left: 1px solid #ebedf3;
  overflow-y: auto;
}

// Trilho central do ziguezague.
.tl {
  position: relative;
  // Respiro extra na base pra o ultimo evento nao encostar no limite do
  // card (o fade da .tl-corpo__lista termina por aqui).
  padding: 0.25rem 1.25rem 1.5rem;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 3px;
    background: #e4e6ef;
    border-radius: 999px;
    transform: translateX(-50%);
  }
}

.tl__separador {
  position: relative;
  z-index: 2;
  text-align: center;
  margin: 0.3rem 0 0.4rem;

  span {
    display: inline-block;
    background: #f3f0fa;
    border-radius: 999px;
    padding: 0.15rem 0.7rem;
    font-size: 0.68rem;
    font-weight: 700;
    color: #472282;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }
}

.tl__item {
  display: grid;
  grid-template-columns: 1fr 56px 1fr;
  align-items: center;
  margin-bottom: 0.25rem;
}

// z-index acima do trilho: sem isso a linha corta a bolha e a hora.
.tl__meio {
  grid-column: 2;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}

.tl__hora {
  font-size: 0.68rem;
  font-weight: 600;
  color: #9a9ab0;
  background: #fff;
  border-radius: 999px;
  padding: 0.05rem 0.3rem;
  font-variant-numeric: tabular-nums;
}

.tl__bolha {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #e4e6ef;
  box-shadow: 0 0 0 4px #fff;

  i {
    font-size: 0.82rem !important;
  }
}

// O card usa o mesmo tom suave dos degraus do Ranking, pra que os dois
// cards da coluna falem a mesma linguagem visual.
.tl__card {
  border: 2.5px solid transparent;
  border-radius: 10px;
  padding: 0.5rem 0.7rem;
  background: #fff;
  min-width: 0;
}

.tl__card--clicavel {
  cursor: pointer;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 3px 10px rgba(60, 50, 90, 0.12);
  }
}

.tl__item--esq .tl__card {
  grid-column: 1;
  grid-row: 1;
}

.tl__item--dir .tl__card {
  grid-column: 3;
  grid-row: 1;
}

.tl__tipo {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  // Caixa alta pede peso moderado e mais espaco entre letras: com 800 e
  // spacing curto o texto fica pesado demais.
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;

  i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background: #fff;
    font-size: 0.72rem !important;
  }
}

.tl__linha {
  font-size: 0.74rem;
  line-height: 1.45;
  min-width: 0;
}

.tl__rotulo {
  color: #9a9ab0;
  font-weight: 500;
}

.tl__valor {
  color: #2b2640;
  font-weight: 600;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tl__rodape {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.4rem;
  min-height: 18px;
}

.tl__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.1rem 0.4rem;
  border-radius: 6px;
  font-size: 0.69rem;
  font-weight: 600;
  background: #f3f6f9;
  color: #7c7c8a;
  font-variant-numeric: tabular-nums;

  i {
    font-size: 0.65rem !important;
  }
}

.tl__badge--erro {
  background: #fde6e8;
  color: #f64e60;
}

.tl__reenviar {
  font-size: 0.68rem !important;
  padding: 0.15rem 0.45rem !important;
}

.tl__reenviado {
  color: #1e7e46;
  font-size: 0.68rem;
}

// Mesma linguagem visual dos botoes de filtro do topo: fundo branco,
// borda fina, so o icone.
.tl__acoes {
  display: inline-flex;
  gap: 0.25rem;
}

.tl__acao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  color: #7c7c8a;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  i {
    font-size: 0.68rem !important;
  }

  &:hover {
    color: #472282;
    border-color: #472282;
  }
}

.tl__seta {
  margin-left: auto;
  color: #b5b5c3;
  font-size: 0.7rem !important;
}

// Cor por tipo aplicada ao icone/bolha/borda do card. As cores sao as
// mesmas do grafico Mensagens do dashboard, pra manter a leitura de
// sentido consistente entre os dois componentes.
@mixin cor-do-tipo($cor, $fundo) {
  .tl__bolha {
    border-color: $cor;

    i {
      color: $cor !important;
    }
  }

  .tl__tipo,
  .tl__tipo i {
    color: $cor !important;
  }

  .tl__card {
    background: $fundo;
    border-color: rgba($cor, 0.35);
  }

  .tl__badge {
    background: rgba($cor, 0.14);
    color: $cor;
  }
}

.tl__item--envio {
  @include cor-do-tipo(#ff7f00, #fff5ea);
}

.tl__item--recebimento {
  @include cor-do-tipo(#472282, #f2eefa);
}

.tl__item--erro {
  @include cor-do-tipo(#f64e60, #fdedee);
}

.tl__item--agendamento {
  @include cor-do-tipo(#00a884, #e3f5f0);
}

.tl__sentinela {
  height: 1px;
}

// Rodape da lista: separa o fim dos eventos com borda e respiro, em vez
// de encostar o ultimo card na base do card.
.tl__status {
  position: relative;
  text-align: center;
  color: #b5b5c3;
  margin-top: 0.75rem;
  padding: 0.75rem 0 0.25rem;
  border-top: 1px solid #ebedf3;

  small {
    background: #fff;
    padding: 0 0.5rem;
  }
}

// Coluna unica em telas estreitas: trilho a esquerda, cards a direita.
@media (max-width: 575px) {
  .tl::before {
    left: 14px;
  }

  .tl__item {
    grid-template-columns: 28px 1fr;
    gap: 0.5rem;
  }

  .tl__meio {
    grid-column: 1;
  }

  .tl__item--esq .tl__card,
  .tl__item--dir .tl__card {
    grid-column: 2;
  }

  .tl__separador {
    text-align: left;
    padding-left: 2px;
  }
}

.card {
  transition: 0.3s ease;

  &:hover {
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  }
}
</style>
